import { companyContactFragment } from './fragments/companyContacts';
import { paginationFragment } from './fragments/pagination';

const userFragment = `
  id
  externalRefId
  idVerification @include(if: $withIdVerification) {
    id
    status
    createdAt
    completedAt
    source
    customerInfo {
      firstName
      lastName
      middleName
      dateOfBirth
      gender
      nationality
      identificationDocument {
        documentType
        issuingCountryCode
        dateOfExpiry
        documentNumbers
      }
      address
      creditScore
    }
    manualVerificationRequired
    manualVerificationReason
    isManuallyVerified
    manualChecks {
      nameMatches
      dobMatches
      idClearAndUnexpired
      faceMatches
    }
  }
  preApproval @include(if: $withPreApproval) {
    id
    amount
    downPayment
    verificationMethod
    documentUrl
    status
    companyId
    createdAt
    rawData {
      status
      id
      price
    }
  }
`;

const fragment = `
  id
  name
  firstName
  lastName
  email
  phone
  createdAt
  updatedAt
  ip
  userAgent
  isIdVerified
  isFinancialVerified
  isHomeVerified
  isPreApprovalVerified
  isOfferConfirmed
  isSignConfirmed
  isDepositConfirmed
  street
  city
  zip
  state
  country
  resolvedCity
  resolvedZip
  resolvedState
  resolvedCountry
  homeAddress
  homeVerifiedAt
  isOwnHome
  isSellingHome
  outstandingMortgageBalance
  totalVisits @include(if: $withTotalVisits)
  averageVisitTime @include(if: $withAverageVisitTime)
  socialMediaType(companyId: $companyId)
  viewedAt
  firstEngagedAt(companyId: $companyId)
  companyContact(companyId: $companyId) {
    ${companyContactFragment}
  }
  party(companyId: $companyId) @include(if: $withParty) {
    id
    affordability @include(if: $withAffordability) {
      maxHomeAffordability
      assessedAt
      monthlyMortgagePayment
      maxRentingAmount
      annualIncome
    } 
  }
  propertyInfo @include(if: $withPropertyInfo)
  homes(companyId: $companyId) @include(if: $withHomes) {
    id
    beds
    baths
    sizeSqft
    totalPrice
    homeUrl
    createdAt
    deletedByUserAt
    singleFamilyHome {
      isQuickPossession
      isLot
      lot
      model {
        id
        name
        imageUrl
        defaultImageUrl
        productType {
          id
          name
        }
      }
      community {
        id
        name
      }
      elevation {
        id
        imageUrl
      }
    }
    multiFamilyHome {
      project {
        id
        name
      }
      unit {
        id
        name
        defaultImageUrl
        buildingModel {
          id
          name
          bedrooms
          bathrooms
          size
        }
      }
    }
  }
  interestedHomes(companyId: $companyId) @include(if: $withInterestedHomes) {
    id
    totalPrice
    singleFamilyHome {
      model {
        id
        name
        productType {
          id
          name
        }
      }
      community {
        id
        name
      }
    }
    multiFamilyHome {
      project {
        id
        name
      }
      unit {
        id
        name
        buildingModel {
          id
          name
        }
      }
    }
  }
  isEmailVerified(companyId: $companyId) @include(if: $withIsEmailVerified)
  companyContacts {
    company {
      id
      name
    }
  }
  lastActivityDate(companyId: $companyId)
  coBuyersCount(companyId: $companyId)
  user @include(if: $withUser) {
    ${userFragment}
  }
  activeHome(companyId: $companyId) @include(if: $withActiveHome) {
    divisionId
    isDealComplete
    offer @include(if: $withActiveOffer) {
      status
      statusChangeDates
      signStatus
      signStatusChangeDates
      manuallyCompletedAt
    }
    deposit @include(if: $withActiveOffer) {
      status
      statusChangeDates
      partialRefund
      fullRefund
    }
  }
  hasFinancialReport @include(if: $withFinancialSummary)
  financialSummary @include(if: $withFinancialSummary) {
    employerPrimaryName
    averageMonthlyFreeCashFlow
    averageMonthlyLoanPaymentsAmount
    nfsTotal
  }
  financialReport @include(if: $withFinancialSummary) {
    ... on FinancialReportFlinks {
      provider
      primaryEmployerIncome {
        trend
      }
      accountAnalysis {
        debitActivityTrend
        creditActivityTrend
        overallActivityTrend
      }
      overallBalance {
        trend
      }
    }
    ... on FinancialReportPlaid {
      provider
    }
    ... on FinancialReportInverite {
      provider
    }
  }
  isAgent
  trafficSourceData(companyId: $companyId) @include(if: $withTrafficSourceData)
  phoneVerifiedAt
`;

export const listCustomersQuery = `
  query ListContacts(
    $filter: ContactFilterInput
    $sort: SortInput
    $page: Int
    $pageSize: Int
    $withTotalVisits: Boolean! = false
    $withAverageVisitTime: Boolean! = false
    $withPropertyInfo: Boolean! = false
    $withAffordability: Boolean! = false
    $withParty: Boolean! = false
    $withIsEmailVerified: Boolean! = true
    $companyId: Int
    $withUser: Boolean! = false
    $withIdVerification: Boolean! = false
    $withPreApproval: Boolean! = false
    $withActiveHome: Boolean! = false
    $withActiveOffer: Boolean! = false
    $withFinancialSummary: Boolean! = false
    $withTrafficSourceData: Boolean! = false
    $withHomes: Boolean! = false
    $withInterestedHomes: Boolean! = true
  ) {
    result: listContacts(filter: $filter, sort: $sort, page: $page, pageSize: $pageSize) {
      items {
        ${fragment}
      }
      pagination {
        ${paginationFragment}
      }
    }
  }
`;

export const getCustomerQuery = `
  query GetContact(
    $id: Int!
    $withTotalVisits: Boolean! = false
    $withAverageVisitTime: Boolean! = false
    $withPropertyInfo: Boolean! = false   
    $withAffordability: Boolean! = true 
    $withParty: Boolean! = true
    $withIsEmailVerified: Boolean! = true
    $companyId: Int
    $withUser: Boolean! = true
    $withIdVerification: Boolean! = true
    $withPreApproval: Boolean! = true
    $withActiveHome: Boolean! = false
    $withActiveOffer: Boolean! = false
    $withFinancialSummary: Boolean! = true
    $withTrafficSourceData: Boolean! = false
    $withHomes: Boolean! = true
    $withInterestedHomes: Boolean! = false
  ) {
    item: getContact(id: $id) {
      ${fragment}
    }
  }
`;

export const updateCustomerQuery = `
  mutation UpdateContact(
    $input: UpdateContactInput!
    $withTotalVisits: Boolean! = false
    $withAverageVisitTime: Boolean! = false
    $withPropertyInfo: Boolean! = false   
    $withAffordability: Boolean! = true 
    $withParty: Boolean! = true
    $withIsEmailVerified: Boolean! = true
    $companyId: Int
    $withUser: Boolean! = true
    $withIdVerification: Boolean! = true
    $withPreApproval: Boolean! = true
    $withActiveHome: Boolean! = false
    $withActiveOffer: Boolean! = false
    $withFinancialSummary: Boolean! = true
    $withTrafficSourceData: Boolean! = false
    $withHomes: Boolean! = true
    $withInterestedHomes: Boolean! = false
  ) {
    item: updateContact(input: $input, companyId: $companyId) {
      ${fragment}
    }
  }
`;

export const countAllPurchasableCustomersQuery = `
  query CountAllPurchasableCustomersQuery(
    $companyId: Int!
  ) {
    count: countPurchasableCustomers(companyId: $companyId)
  }
`;

export const createCustomerQuery = `
  mutation CreateCustomer(
    $input: BaseCreateContactInput!
    $withTotalVisits: Boolean! = false
    $withAverageVisitTime: Boolean! = false
    $withPropertyInfo: Boolean! = false   
    $withAffordability: Boolean! = true
    $withParty: Boolean! = true
    $withIsEmailVerified: Boolean! = true
    $companyId: Int
    $withUser: Boolean! = false
    $withIdVerification: Boolean! = false
    $withPreApproval: Boolean! = false
    $withActiveHome: Boolean! = false
    $withActiveOffer: Boolean! = false
    $withFinancialSummary: Boolean! = true
    $withTrafficSourceData: Boolean! = false
    $withHomes: Boolean! = true
    $withInterestedHomes: Boolean! = false
  ) {
    item: createContact(input: $input) {
      ${fragment}
    }
  }
`;

export const syncCustomerPropertyInfoMutation = `
  mutation SyncContactPropertyInfo(
    $id: Int!
    $withTotalVisits: Boolean! = false
    $withAverageVisitTime: Boolean! = false
    $withPropertyInfo: Boolean! = false   
    $withAffordability: Boolean! = true
    $withParty: Boolean! = true
    $withIsEmailVerified: Boolean! = true
    $companyId: Int
    $withUser: Boolean! = true
    $withIdVerification: Boolean! = true
    $withPreApproval: Boolean! = true
    $withActiveHome: Boolean! = false
    $withActiveOffer: Boolean! = false
    $withFinancialSummary: Boolean! = true
    $withTrafficSourceData: Boolean! = false
    $withHomes: Boolean! = true
    $withInterestedHomes: Boolean! = false
  ) {
    item: syncContactPropertyInfo(id: $id) {
      ${fragment}
    }
  }
`;

export const sendVerifiedInvitationMutation = `
  mutation SendVerifiedInvitation(
    $companyId: Int!
    $ids: [Int!]!
    $inviteMethods: [String]
  ) {
    sent: sendVerifiedInvitation(companyId: $companyId, ids: $ids, inviteMethods: $inviteMethods)
  }
`;

export const sendReserveNowInvitationMutation = `
  mutation SendReserveNowInvitation(
    $companyId: Int!
    $contactIds: [Int!]!
    $inviteMethods: [String]
    $homeId: Int!
    $homeType: String!
    $isEmbeddedInvitation: Boolean! = false
  ) {
    sent: sendReserveNowInvitation(companyId: $companyId, contactIds: $contactIds, inviteMethods: $inviteMethods, homeId: $homeId, homeType: $homeType, isEmbeddedInvitation: $isEmbeddedInvitation)
  }
`;

export const resetCustomerVerification = `
  mutation ResetContactVerifications(
    $id: Int!
    $withTotalVisits: Boolean! = false
    $withAverageVisitTime: Boolean! = false
    $withPropertyInfo: Boolean! = false   
    $withAffordability: Boolean! = true 
    $withParty: Boolean! = true
    $withIsEmailVerified: Boolean! = true
    $companyId: Int!
    $withUser: Boolean! = false
    $withIdVerification: Boolean! = false
    $withPreApproval: Boolean! = false
    $withActiveHome: Boolean! = false
    $withActiveOffer: Boolean! = false
    $withFinancialSummary: Boolean! = true
    $withTrafficSourceData: Boolean! = false
    $withHomes: Boolean! = true
    $withInterestedHomes: Boolean! = false
  ) {
    item: resetContactVerifications(id: $id, companyId: $companyId) {
      ${fragment}
    }
  }
`;

export const findContactQuery = `
  query FindContact(
    $email: String!
    $companyId: Int!
    $withTotalVisits: Boolean! = false
    $withAverageVisitTime: Boolean! = false
    $withPropertyInfo: Boolean! = false   
    $withAffordability: Boolean! = false 
    $withParty: Boolean! = false
    $withIsEmailVerified: Boolean! = true
    $withUser: Boolean! = false
    $withIdVerification: Boolean! = false
    $withPreApproval: Boolean! = false
    $withActiveHome: Boolean! = false
    $withActiveOffer: Boolean! = false
    $withFinancialSummary: Boolean! = false
    $withTrafficSourceData: Boolean! = false
    $withHomes: Boolean! = true
    $withInterestedHomes: Boolean! = false
  ) {
    item: findContact(email: $email, companyId: $companyId) {
      ${fragment}
    }
  }
`;

export const listCustomersLiteQuery = `
  query ListContacts(
    $filter: ContactFilterInput
    $sort: SortInput
    $page: Int
    $pageSize: Int
    $companyId: Int!
  ) {
    result: listContacts(filter: $filter, sort: $sort, page: $page, pageSize: $pageSize) {
      items {
        id
        name
        firstName
        lastName
        email
        phone
        viewedAt
        companyContact(companyId: $companyId) {
          createdAt
        }
      }
      pagination {
        ${paginationFragment}
      }
    }
  }
`;

export const getCustomerFinancialReportQuery = `
  query ContactFinancialReport(
    $contactId: Int!
  ) {
    report: contactFinancialReport(contactId: $contactId) {
      ... on FinancialReportFlinks {
        provider
        primaryEmployerIncome {
          name
          trend
          frequency
          income
          monthlyIncomeAmounts
          monthlyIncomeCounts
          last2Paydates
        }
        secondaryEmployerIncome {
          name
          trend
          frequency
          income
          monthlyIncomeAmounts
          monthlyIncomeCounts
          last2Paydates
        }
        monthlyIncomeTotalAmounts
        monthlyIncomeTotalCounts
        averageEmployerIncomeDeposit
        estimatedAnnualNetEmployerIncome
        nonEmployerIncome
        governmentIncome {
          currentIncome
          averageDeposit
          frequency
          last2Paydates
          totalIncome
        }
        accountAnalysis {
          activeDaysInAccount
          firstDetectedTransaction
          averageMonthlyFreeCashFlow
          averageMonthlyRecurringPayments
          averageMonthlyRecurringDeposits
          activeDaysTrend
          debitActivityTrend
          creditActivityTrend
          overallActivityTrend
        }
        overallBalance {
          trend
          max
          min
          current
          balance90DaysAgo
          daysWithNegativeBalance
        }
        closingBalance {
          dayOfIncome
          dayOfEmployerIncome
          dayOfGovernmentIncome
          dayAfterIncome
          dayAfterEmployerIncome
          dayAfterGovernmentIncome
        }
        nsfFee {
          nfsTotal
          nfsOf60Days
          nfsOf6Months
          nfsMonthlyAverage
          nfsDaysAfterEmployerIncome
          nfsDaysAfterGovernmentIncome
        }
        allLoanPayments {
          loanPaymentList {
            loanType
            monthlyPaymentAmounts
            monthlyPaymentCounts
          }
        }
        monthlyLoanPaymentTotalAmounts
        monthlyLoanPaymentTotalCounts
        averageMonthlyLoanPaymentsAmount
        averageMonthlyMicroLoanPaymentsAmount
        averageMonthlyMortgagePaymentsAmount
        averageMonthlyAutomotiveLoanPaymentsAmount
        averageMonthlyStudentLoanPaymentsAmount
        averageMonthlyOtherLoanPaymentsAmount
        liabilityPayments
      }
      ... on FinancialReportPlaid {
        provider
        incomeWages
        incomeDividends
        incomeInterestEarned
        incomeRetirementPension
        incomeOtherIncome
        loanPaymentsCarPayment
        loanPaymentsPersonalLoanPayment
        loanPaymentsStudentLoanPayment
        loanPaymentsOtherPayment
        loanPaymentsCreditCardPayment
      }
      ... on FinancialReportInverite {
        provider
        reportUrl
      }
    }
  }
`;

export const listContactRegionsQuery = `
  query ListContactRegions(
    $companyId: Int!
  ) {
    items: listContactRegions(companyId: $companyId)
  }
`;

export const exportCustomersQuery = `
  query ExportContacts(
    $filter: ContactFilterInput
  ) {
    version: exportContacts(filter: $filter)
  }
`;

export const startContactInsightsGenerationMutation = `
  mutation StartContactInsightsGeneration(
    $id: Int!
    $companyId: Int!
    $forceGeneration: Boolean! = false
  ) {
    requestId: startContactInsightsGeneration(id: $id, companyId: $companyId, forceGeneration: $forceGeneration)
  }
`;
